import React from 'react'
import { ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { localized } from '@/decorators/localized'
import context from '@/context'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { openDialog } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import { reportSubmissionGroupExportType } from '@/enums/reportSubmissionGroupExportType'
import { downloadFile } from '@/utils/downloader'
import { xsdSchemaTypes } from '@/enums/xsdSchemaTypes'
import { withPermissions } from '@/decorators/withPermissions'
import { OutputReportGroupState } from '@/enums/outputReportGroupState'

const fileFormats = {
  excel: 0,
  xml: 1
}
@localized()
@withPermissions()
class OutputReportDownloadButtons extends React.Component {
  static propTypes = {
    localize: PropTypes.any,
    openDialog: PropTypes.func,
    outputReportGroup: PropTypes.any
  };

  constructor (props, context) {
    super(props, context)

    this.state = {
      downloadXlsOpen: false,
      downloadXmlOpen: false
    }
  }

  displayError = () => {
    this.props.openDialog({
      dialogType: dialogTypes.alert,
      title: 'Reporting.GeneratingExcel',
      content: 'Reporting.GeneratingExcelError'
    })
  }

  confirmDialog = (action) => {
    this.props.openDialog({
      dialogType: dialogTypes.confirm,
      title: 'Reporting.GenerateExcel',
      content: 'Reporting.ConfirmGenerateExcel',
      onAction: action
    })
  }

  onDownloadError = () => {
    this.props.openDialog({
      dialogType: dialogTypes.alert,
      title: 'Reporting.GenerateExcel',
      content: 'Reporting.ExportDownloadError'
    })
  }

  downloadArabicEmptyXlsx = () => { this.downloadEmptyXlsx('ar') }
  downloadEnglishEmptyXlsx = () => { this.downloadEmptyXlsx('en') }
  downloadEmptyXlsx = (languageName) => {
    this.downloadXlsx(
      languageName,
      reportSubmissionGroupExportType.enum.Clean,
      false
    )
  }
  downloadArabicCurrentDataXlsx = () => { this.downloadDataXlsx(reportSubmissionGroupExportType.enum.CurrentSubmission, 'ar') }
  downloadEnglishCurrentDataXlsx = () => { this.downloadDataXlsx(reportSubmissionGroupExportType.enum.CurrentSubmission, 'en') }
  downloadDataXlsx = (exportType, languageName) => {
    if (this.props.outputReportGroup.outputReportGroupState === 1) {
      this.downloadXlsx(
        languageName,
        exportType,
        true
      )
    } else {
      // start export job
      this.confirmDialog(
        () => context.api.reportExportService.exportOutputReportGroupAsExcelFile({ Id: this.props.outputReportGroup.id, ExportType: exportType, EnglishTranslation: languageName === 'en' })
      )
    }
  }

  downloadXlsx = (languageName, exportType, isAccepted) => {
    const fileAttachmentId = this.findFileAttachment(languageName, exportType, fileFormats.excel, isAccepted)
    if (fileAttachmentId) {
      const input = {
        reportSubmissionGroupId: this.props.outputReportGroup.id,
        fileAttachmentId
      }
      downloadFile(input, context.api.outputReportGroupService.downloadExport, null, null, this.onDownloadError)
    }
  }

  downloadEmptyXml = () => { this.downloadXml(reportSubmissionGroupExportType.enum.Clean, false) }

  downloadCurrentDataXml = () => { this.downloadDataXml(reportSubmissionGroupExportType.enum.CurrentSubmission) }

  downloadDataXml = (exportType) => {
    if (this.props.outputReportGroup.outputReportGroupState === 1) {
      this.downloadXml(
        exportType,
        true
      )
    } else {
      // start export job
      this.generateXml(exportType)
    }
  }

  generateXml = (exportType) => {
    const id = this.props.outputReportGroup.id
    this.confirmDialog(() => context.api.reportExportService.exportOutputReportGroupAsXmlFile({ Id: id, ExportType: exportType }))
  }

  downloadXml = (exportType, isAccepted) => {
    const fileAttachmentId = this.findFileAttachment('en', exportType, fileFormats.xml, isAccepted)
    if (fileAttachmentId) {
      const input = {
        reportSubmissionGroupId: this.props.outputReportGroup.id,
        fileAttachmentId
      }
      downloadFile(input, context.api.outputReportGroupService.downloadExport, null, null, this.onDownloadError)
    }
  }

  downloadXsd = () => {
    downloadFile(xsdSchemaTypes.enum.ReportSubmissionGroupXml, context.api.reportExportService.downloadXsdSchema, null, null, this.onDownloadError)
  }

  findFileAttachment = (languageName, exportType, exportFormat, isAccepted) => {
    const { exportDownloads } = this.props.outputReportGroup
    if (exportDownloads && exportDownloads.length > 0) {
      const exportedFile = exportDownloads.find(x =>
        x.languageName === languageName &&
        x.exportType === exportType &&
        x.exportFormat === exportFormat &&
        x.isAccepted === isAccepted
      )
      if (exportedFile) { return exportedFile.fileAttachmentId }
    }
    return null
  }
  hasExport = (languageName, exportType, exportFormat, isAccepted) => {
    return this.findFileAttachment(languageName, exportType, exportFormat, isAccepted) !== null
  }

  render () {
    const { outputReportGroup, localize } = this.props
    const isEditable = outputReportGroup.outputReportGroupState === OutputReportGroupState.enum.Editable

    if (!outputReportGroup) { return null }
    return (
      <div className='DownloadButtons button-section-alternative'>
        <ButtonDropdown isOpen={this.state.downloadXlsOpen} toggle={() => this.setState({ downloadXlsOpen: !this.state.downloadXlsOpen })}>
          <DropdownToggle caret>
            {localize('Reporting.DownloadXls')}
          </DropdownToggle>
          <DropdownMenu>
            {isEditable && <React.Fragment>
              <DropdownItem
                disabled={!this.hasExport('en', reportSubmissionGroupExportType.enum.Clean, fileFormats.excel, false)}
                onClick={this.downloadEnglishEmptyXlsx}>{localize('Reporting.EmptyTemplateEnglish')}</DropdownItem>
              <DropdownItem
                disabled={!this.hasExport('ar', reportSubmissionGroupExportType.enum.Clean, fileFormats.excel, false)}
                onClick={this.downloadArabicEmptyXlsx}>{localize('Reporting.EmptyTemplateArab')}</DropdownItem>
            </React.Fragment>}
            <DropdownItem
              onClick={this.downloadEnglishCurrentDataXlsx}>
              {localize('Reporting.CurrentDataEnglish')}
            </DropdownItem>
            <DropdownItem
              onClick={this.downloadArabicCurrentDataXlsx}>
              {localize('Reporting.CurrentDataArab')}
            </DropdownItem>
          </DropdownMenu>
        </ButtonDropdown>
        <ButtonDropdown isOpen={this.state.downloadXmlOpen} toggle={() => this.setState({ downloadXmlOpen: !this.state.downloadXmlOpen })}>
          <DropdownToggle caret>
            {localize('Reporting.DownloadXml')}
          </DropdownToggle>
          <DropdownMenu>
            {isEditable && <DropdownItem
              disabled={!this.hasExport('en', reportSubmissionGroupExportType.enum.Clean, fileFormats.xml, false)}
              onClick={this.downloadEmptyXml}>{localize('Reporting.EmptyTemplate')}</DropdownItem>}
            <DropdownItem
              onClick={this.downloadCurrentDataXml}>
              {localize('Reporting.CurrentData')}
            </DropdownItem>
          </DropdownMenu>
        </ButtonDropdown>
      </div>
    )
  }
}

const mapDispatchToProps = {
  openDialog
}

export default connect(null, mapDispatchToProps)(OutputReportDownloadButtons)



// WEBPACK FOOTER //
// ./src/internal-app/pages/outputReports/outputReportDownloadButtons.js